import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import MonthSelector, { currentMonthKey } from "@/components/MonthSelector";
import {
  FileUp,
  FileText,
  Download,
  Trash2,
  Package,
  Loader2,
  FileX,
  Archive,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function AdminCediblePage() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openUpload, setOpenUpload] = useState(false);
  const [selectedClient, setSelectedClient] = useState("");
  const [pickedFiles, setPickedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [filterClient, setFilterClient] = useState("ALL");
  const [filterMes, setFilterMes] = useState("ALL");
  const [uploadMes, setUploadMes] = useState(currentMonthKey());
  const [zipping, setZipping] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: u }] = await Promise.all([
        api.get("/cedible"),
        api.get("/admin/users"),
      ]);
      setItems(c);
      setClients(u.filter((x) => x.role === "client"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    if (!selectedClient) {
      toast.error("Selecciona un cliente");
      return;
    }
    if (pickedFiles.length === 0) {
      toast.error("Selecciona al menos un PDF");
      return;
    }
    const fd = new FormData();
    pickedFiles.forEach((f) => fd.append("files", f));
    fd.append("mes", uploadMes);
    setUploading(true);
    try {
      const { data } = await api.post(
        `/admin/clients/${selectedClient}/cedible`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`${data.count} cedible(s) subido(s) correctamente`);
      setOpenUpload(false);
      setPickedFiles([]);
      setSelectedClient("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
    }
  };

  const download = async (r) => {
    try {
      const res = await api.get(`/cedible/${r.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.filename || `Cedible_${r.folio || r.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Error al descargar");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/cedible/${id}`);
      toast.success("Cedible eliminado");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const filtered = items.filter((r) => {
    if (filterClient !== "ALL" && r.user_id !== filterClient) return false;
    if (filterMes !== "ALL" && (r.mes || "") !== filterMes) return false;
    return true;
  });

  const availableMonths = Array.from(
    new Set(items.map((r) => r.mes).filter(Boolean))
  ).sort().reverse();

  const downloadZip = async () => {
    if (filterClient === "ALL" && filterMes === "ALL") {
      toast.error("Selecciona al menos un filtro (cliente o mes) para armar el ZIP");
      return;
    }
    setZipping(true);
    try {
      const params = {};
      if (filterClient !== "ALL") params.user_id = filterClient;
      if (filterMes !== "ALL") params.mes = filterMes;
      const res = await api.get("/admin/cedible/download-zip", {
        params,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const suffix = [
        filterClient !== "ALL"
          ? clients.find((c) => c.id === filterClient)?.id_cliente ||
            clients.find((c) => c.id === filterClient)?.razon_social ||
            "cliente"
          : null,
        filterMes !== "ALL" ? filterMes.replace("-", "") : null,
      ]
        .filter(Boolean)
        .join("_");
      a.download = `CEDIBLES_${suffix || "todos"}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${filtered.length} cedible(s) descargados en ZIP`);
    } catch (e) {
      const detail =
        e.response?.data instanceof Blob
          ? await e.response.data
              .text()
              .then((t) => {
                try {
                  return JSON.parse(t).detail;
                } catch {
                  return t;
                }
              })
          : formatApiError(e.response?.data?.detail);
      toast.error(detail || "Error al armar el ZIP");
    } finally {
      setZipping(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-6 md:p-10">
        <PageHeader
          eyebrow="Administración · Cedibles"
          title="CEDIBLE — Guías de Despacho (PDF)"
          description="Sube las guías de despacho en PDF (copia CEDIBLE) para que los clientes puedan descargarlas desde su portal."
          actions={
            <button
              onClick={() => setOpenUpload(true)}
              data-testid="cedible-upload-btn"
              className="flex items-center gap-2 px-5 py-3 bg-[#D95A40] hover:bg-[#c94d33] text-white font-bold uppercase tracking-[0.15em] text-xs transition-colors"
            >
              <FileUp className="w-4 h-4" />
              Subir Cedible
            </button>
          }
        />

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="w-full md:w-72">
            <Select value={filterClient} onValueChange={setFilterClient}>
              <SelectTrigger data-testid="cedible-filter-client">
                <SelectValue placeholder="Filtrar por cliente" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                <SelectItem value="ALL">Todos los clientes</SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.razon_social || c.name} — {c.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-full md:w-56">
            <Select value={filterMes} onValueChange={setFilterMes}>
              <SelectTrigger data-testid="cedible-filter-mes">
                <SelectValue placeholder="Filtrar por mes" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                <SelectItem value="ALL">Todos los meses</SelectItem>
                {availableMonths.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <button
            onClick={downloadZip}
            disabled={
              zipping ||
              filtered.length === 0 ||
              (filterClient === "ALL" && filterMes === "ALL")
            }
            data-testid="cedible-download-zip-btn"
            className="ml-auto flex items-center gap-2 px-4 py-2 bg-[#0B1B3D] hover:bg-[#D95A40] disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold uppercase tracking-[0.15em] transition-colors"
            title={
              filterClient === "ALL" && filterMes === "ALL"
                ? "Selecciona cliente o mes para descargar el ZIP"
                : `Descargar ${filtered.length} cedible(s) en un ZIP`
            }
          >
            {zipping ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Archive className="w-4 h-4" />
            )}
            {zipping ? "Armando ZIP..." : `Descargar ZIP (${filtered.length})`}
          </button>
        </div>

        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0B1B3D] text-white">
              <tr>
                <Th>Cliente</Th>
                <Th>Archivo</Th>
                <Th>Folio GD</Th>
                <Th className="text-right">Tamaño</Th>
                <Th>Subido por</Th>
                <Th>Fecha</Th>
                <Th className="text-right">Acciones</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr>
                  <td colSpan={7} className="p-10 text-center text-slate-500">
                    Cargando...
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-10 text-center text-slate-500">
                    Aún no se han subido cedibles PDF.
                  </td>
                </tr>
              )}
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`cedible-row-${r.id}`}
                  className="hover:bg-slate-50"
                >
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#0B1B3D]">
                      {r.razon_social || r.user_name}
                    </div>
                    <div className="text-xs text-slate-500 font-mono">
                      {r.user_email}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 text-[#0B1B3D]">
                      <FileText className="w-4 h-4 text-red-500 shrink-0" />
                      <span className="truncate max-w-xs">{r.filename}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-[#D95A40] font-semibold">
                    {r.folio || "—"}
                    {r.mes && (
                      <div className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-700 font-bold px-1.5 py-0.5 inline-block mt-1 font-sans">
                        {r.mes}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-xs text-slate-600">
                    {((r.size_bytes || 0) / 1024).toFixed(1)} KB
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs font-mono">
                    {r.uploaded_by}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">
                    {new Date(r.uploaded_at).toLocaleString("es-CL")}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        onClick={() => download(r)}
                        data-testid={`cedible-download-${r.id}`}
                        className="p-2 bg-[#0B1B3D] hover:bg-[#D95A40] text-white transition-colors"
                        title="Descargar"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button
                            data-testid={`cedible-delete-${r.id}`}
                            className="p-2 hover:bg-red-50 text-slate-500 hover:text-red-600"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              ¿Eliminar cedible?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              El cliente perderá el acceso a este PDF.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => remove(r.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Eliminar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload dialog */}
      <Dialog open={openUpload} onOpenChange={setOpenUpload}>
        <DialogContent className="bg-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tighter">
              Subir Cedibles PDF
            </DialogTitle>
            <DialogDescription>
              Selecciona el cliente destino y uno o varios archivos PDF (copia CEDIBLE de la guía de despacho). El folio se auto-detecta.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600 block mb-1.5">
                Cliente destino
              </label>
              <Select
                value={selectedClient}
                onValueChange={setSelectedClient}
              >
                <SelectTrigger data-testid="cedible-select-client">
                  <SelectValue placeholder="Selecciona un cliente" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.razon_social || c.name} — {c.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600 block mb-1.5">
                Mes correspondiente <span className="text-[#D95A40]">*</span>
              </label>
              <MonthSelector
                value={uploadMes}
                onChange={setUploadMes}
                testid="cedible-mes-selector"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600 block mb-1.5">
                PDFs de guías de despacho (uno o varios)
              </label>
              <input
                type="file"
                accept=".pdf,application/pdf"
                multiple
                onChange={(e) =>
                  setPickedFiles(Array.from(e.target.files || []))
                }
                data-testid="cedible-file-input"
                className="block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[#0B1B3D] file:text-white file:font-bold file:uppercase file:tracking-wider file:text-xs hover:file:bg-[#D95A40] file:cursor-pointer border border-slate-300 py-1"
              />
              {pickedFiles.length > 0 && (
                <ul className="mt-3 border border-slate-200 divide-y divide-slate-100 max-h-40 overflow-auto">
                  {pickedFiles.map((f, i) => (
                    <li
                      key={`${f.name}-${i}`}
                      className="flex items-center justify-between px-3 py-2 text-xs"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-3.5 h-3.5 text-red-500 shrink-0" />
                        <span className="truncate">{f.name}</span>
                        <span className="text-slate-400 tabular-nums">
                          {(f.size / 1024).toFixed(1)} KB
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setPickedFiles((p) => p.filter((_, k) => k !== i))
                        }
                        className="text-slate-400 hover:text-red-500"
                      >
                        <FileX className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => {
                setOpenUpload(false);
                setPickedFiles([]);
              }}
              className="px-4 py-2 border border-slate-300 text-sm hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              onClick={submit}
              disabled={
                uploading || pickedFiles.length === 0 || !selectedClient
              }
              data-testid="cedible-submit-btn"
              className="flex items-center gap-2 px-5 py-2 bg-[#D95A40] hover:bg-[#c94d33] disabled:opacity-50 text-white text-sm font-bold uppercase tracking-wider"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Subiendo...
                </>
              ) : (
                <>
                  <FileUp className="w-4 h-4" /> Subir {pickedFiles.length || ""}
                </>
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.15em] ${className}`}
    >
      {children}
    </th>
  );
}
